import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Copy } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useToast } from '@/hooks/use-toast';
import { AuditLog } from '@/types/audit';

interface AuditLogWithProfile extends AuditLog {
  username?: string;
  avatar_url?: string;
}

interface AuditLogDetailsModalProps {
  log: AuditLogWithProfile;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const AuditLogDetailsModal = ({ log, open, onOpenChange }: AuditLogDetailsModalProps) => {
  const { toast } = useToast();

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copiado!',
      description: 'Dados copiados para a área de transferência',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto bg-gray-900 border-white/10 text-white">
        <DialogHeader>
          <DialogTitle className="text-white">Detalhes do Log de Auditoria</DialogTitle>
          <DialogDescription className="text-white/60">
            Informações completas do evento registrado
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* User Info */}
          <div className="flex items-center gap-4 p-4 border border-white/10 rounded-lg">
            <Avatar className="h-12 w-12">
              <AvatarImage src={log.avatar_url} alt={log.username || 'User'} />
              <AvatarFallback className="bg-white/10 text-white">
                {log.username?.substring(0, 2).toUpperCase() || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-medium text-white">{log.username || 'Usuário'}</p>
              <p className="text-sm text-white/60">ID: {log.user_id}</p>
            </div>
          </div>

          {/* Event Info */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">Tipo de Evento</span>
              <Badge variant="outline" className="text-white border-white/20">{log.event_type}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-white/60">Data e Hora</span>
              <span className="text-sm text-white/90">
                {format(new Date(log.created_at), "dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss", {
                  locale: ptBR,
                })}
              </span>
            </div>
            {log.client_ip && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/60">Endereço IP</span>
                <span className="text-sm font-mono text-white/90">{log.client_ip}</span>
              </div>
            )}
            {log.session_id && (
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/60">ID da Sessão</span>
                <span className="text-sm font-mono text-white/90">{log.session_id.substring(0, 8)}...</span>
              </div>
            )}
          </div>

          {/* User Agent */}
          {log.user_agent && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/60">User Agent</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(log.user_agent!)}
                  className="text-white hover:text-white hover:bg-white/10"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-3 bg-muted rounded-lg text-xs font-mono break-all text-gray-900">
                {log.user_agent}
              </div>
            </div>
          )}

          {/* Location Data */}
          {log.location_data && Object.keys(log.location_data).length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/60">Dados de Localização</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(JSON.stringify(log.location_data, null, 2))}
                  className="text-white hover:text-white hover:bg-white/10"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <pre className="text-xs overflow-x-auto text-gray-900">
                  {JSON.stringify(log.location_data, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Metadata */}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-white/60">Metadados</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(JSON.stringify(log.metadata, null, 2))}
                  className="text-white hover:text-white hover:bg-white/10"
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <pre className="text-xs overflow-x-auto text-gray-900">
                  {JSON.stringify(log.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
